import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import api, { mediaUrl } from "@/lib/api";
import { MapPin, Calendar, Trophy, ArrowRight, HelpCircle } from "lucide-react";
import TeamLeaderboard from "@/components/TeamLeaderboard";

const TABS = ["All", "Upcoming", "Ongoing", "Completed"];

export const STATUS_STYLE = {
  live: { label: "LIVE", color: "hsl(var(--accent))", pulse: true },
  registration: { label: "REGISTRATION OPEN", color: "#16a34a" },
  upcoming: { label: "UPCOMING", color: "hsl(var(--primary))" },
  completed: { label: "COMPLETED", color: "#6b7280" },
};

export default function Tournaments() {
  const [tab, setTab] = useState("All");
  const [list, setList] = useState([]);
  const navigate = useNavigate();

  const load = (t) => {
    const params = {};
    if (t !== "All") params.status = t;
    api.get("/tournaments", { params }).then((r) => setList(r.data.items)).catch(() => {});
  };
  useEffect(() => { load(tab); }, [tab]);

  const fmtDate = (s) => (s ? new Date(s).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "TBD");

  return (
    <div>
      <div className="mx-auto max-w-7xl px-4 md:px-6 pt-8 flex flex-wrap items-center gap-3" data-testid="tournaments-actions">
        <Link to="/tournaments/create" data-testid="tournaments-create-btn" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-white text-black text-sm font-bold hover:gap-3 transition-all">Create Tournament <ArrowRight size={16} /></Link>
        <Link to="/help#tournaments" data-testid="tournaments-howto-btn" className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-semibold text-white/70 hover:text-white underline-offset-4 hover:underline transition-colors"><HelpCircle size={15} /> How to create and manage tournament</Link>
      </div>

      <div className="mx-auto max-w-7xl px-4 md:px-6 py-8 grid lg:grid-cols-[minmax(0,1fr)_340px] gap-8 items-start">
        <div className="min-w-0">
        <div className="flex flex-wrap gap-2 mb-6">
          {TABS.map((t) => (
            <button key={t} onClick={() => setTab(t)} data-testid={`tourn-tab-${t.toLowerCase()}`}
              className={`px-4 py-1.5 rounded-full text-sm font-semibold transition-colors ${tab === t ? "bg-white text-black" : "card-surface text-white/60 hover:text-white"}`}>
              {t}
            </button>
          ))}
        </div>

        {list.length === 0 ? (
          <div className="py-16 text-center text-white/40">No tournaments in this category.</div>
        ) : (
          <div className="space-y-4">
            {list.map((t) => {
              const st = STATUS_STYLE[t.status] || STATUS_STYLE.upcoming;
              return (
                <div key={t.slug} onClick={() => navigate(`/tournaments/${t.slug}`)} data-testid={`tourn-card-${t.slug}`}
                  className="card-surface rounded-2xl p-3 sm:p-4 flex flex-col md:flex-row gap-4 md:gap-5 md:items-center hover-rise cursor-pointer">
                  <div className="img-zoom w-full md:w-48 h-44 sm:h-52 md:h-28 rounded-xl overflow-hidden shrink-0">
                    <img src={mediaUrl(t.cover)} alt={t.name} className="w-full h-full object-cover" loading="lazy" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <span className={`eyebrow px-2 py-0.5 rounded text-white ${st.pulse ? "live-dot" : ""}`} style={{ background: st.color }}>{st.label}</span>
                    <h3 className="mt-2 text-lg sm:text-xl font-bold text-white break-words">{t.name}</h3>
                    <p className="text-sm text-white/50">{t.game}</p>
                    <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs text-white/50">
                      <span className="flex items-center gap-1.5"><Calendar size={13} /> {fmtDate(t.start_date)} – {fmtDate(t.end_date)}</span>
                      <span className="flex items-center gap-1.5"><MapPin size={13} /> {t.location}</span>
                    </div>
                  </div>
                  <div className="flex items-center justify-between gap-3 border-t border-white/5 pt-3 md:border-0 md:pt-0 md:block md:text-right shrink-0">
                    <div>
                      <div className="flex items-center gap-1.5 text-white/50 text-xs md:justify-end"><Trophy size={13} /> Prize Pool</div>
                      <div className="text-xl sm:text-2xl font-bold font-head text-white">{t.prize_pool || "—"}</div>
                    </div>
                    <span className="inline-flex items-center gap-1 text-xs font-semibold md:mt-2" style={{ color: "hsl(var(--primary))" }}>View details <ArrowRight size={12} /></span>
                  </div>
                </div>
              );
            })}
          </div>
        )}
        </div>
        <aside className="lg:sticky lg:top-24" data-testid="tournaments-leaderboard"><TeamLeaderboard /></aside>
      </div>
    </div>
  );
}
